import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment, { Moment } from 'moment';
import {
  Typography,
  Button,
  Input,
  InputPrice,
  BorderLine,
  DatePicker,
  enqueueSnackbar,
  CheckboxLabel,
} from '../../design-system';
import { DashboardHeader, DashboardForm, DashboardFormCustomColumn } from '../dashboard';
import { useSalesFeeAddMutation, useSalesFeeForm } from '../../hooks';
import { formatContainerType, getNormalFormProps } from '../../utils';
import { getCache } from '../../services';
import { MESSAGES, INPUT_ERRORS, containerTypes } from '../../const';
import { SalesFeeAddCache, SalesFeeAddFormProps, ContainerType, FeeType } from '../../types';
import { i18n } from '../../i18n';

let formProps = {} as SalesFeeAddFormProps;

const getSubmitProps = () => {
  const validate = {
    ...Object.fromEntries(
      containerTypes.map((containerType) => [
        containerType.value,
        String(formProps[containerType.value]).length !== 0,
      ]),
    ),
  };

  if ([...Object.values(validate)].some((val) => !val)) {
    return;
  }

  return getNormalFormProps(formProps);
};

export const SalesFeesAddHeader = ({ showOnlySubmit }: any) => {
  const params = useParams();
  const navigate = useNavigate();
  const { mutate } = useSalesFeeAddMutation();

  const handleSubmitClick = () => {
    formProps.onSubmitClick();
    const submitProps = getSubmitProps();
    if (!submitProps) return;

    mutate(
      { ...submitProps, id: params.id },
      {
        onSuccess: () => {
          enqueueSnackbar(MESSAGES.SALES_FEE.SUCCESS.ADDED, 'success');
          navigate('/dashboard/sales-fees');
        },
      },
    );
  };

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {i18n.t('pages.salesFeesAdd.title')}
          </Typography>
          <Button size={'medium'} data-color={'white'} to={-1}>
            {i18n.t('button.goBack')}
          </Button>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} onClick={handleSubmitClick}>
        {i18n.t('button.submit')}
      </Button>
    </DashboardHeader>
  );
};

export const SalesFeesAddForm = () => {
  const [yardName] = useState(getCache<SalesFeeAddCache>('sales-fee-add')?.yardName);
  const [isPercentageFee, setIsPercentageFee] = useState(false);
  const [{ effectiveDate, ...containers }, setFeeState] = useSalesFeeForm({
    effectiveDate: moment(),
  });

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  useEffect(() => {
    formProps = {
      ...formProps,
      yardName,
      effectiveDate,
      feeType: isPercentageFee ? FeeType.PERCENTAGE : FeeType.FIXED,
      ...containers,
      onSubmitClick: () => {
        setIsSubmitClicked(true);
      },
    };
  }, [
    yardName,
    effectiveDate,
    isPercentageFee,
    ...Object.keys(containers).map((containerType) => containers[containerType as ContainerType]),
  ]);

  const handleEffectiveDateClick = (pricingDate: Moment) => {
    setFeeState((props) => {
      props.effectiveDate = pricingDate;
    });
  };

  return (
    <DashboardForm paperSize={'full-size'}>
      <DashboardFormCustomColumn columns={'1fr 1fr'}>
        <Input
          label={i18n.t('common.yard')}
          data-size={'medium'}
          defaultValue={yardName}
          readOnly
        />
        <DatePicker
          label={i18n.t('label.effectiveDate')}
          data-size={'medium'}
          defaultValue={effectiveDate}
          onChange={handleEffectiveDateClick}
          minDate={moment()}
          withoutClearIcon
        />
        <CheckboxLabel
          label={i18n.t('label.percentageFee')}
          checked={isPercentageFee}
          onChange={(e) => setIsPercentageFee(e.target.checked)}
        />
      </DashboardFormCustomColumn>
      <BorderLine space={'dashboard-form'} />
      {Array.from({ length: Math.ceil(containerTypes.length / 2) }, (_, rowIndex) => (
        <DashboardFormCustomColumn
          key={`container-row-${rowIndex}`}
          columns={'1fr 1fr'}
          align={'start'}
        >
          {containerTypes.slice(rowIndex * 2, rowIndex * 2 + 2).map(({ value: key }) => (
            <InputPrice
              key={key}
              label={formatContainerType(key)}
              data-size={'medium'}
              defaultValue={containers[key]}
              onChange={(e) => {
                setFeeState((props) => {
                  props[key] = Number(e.target.value);
                });
              }}
              isPercent={isPercentageFee}
              error={isSubmitClicked ? !String(containers[key]).length : false}
              helperText={
                isSubmitClicked
                  ? !String(containers[key]).length
                    ? INPUT_ERRORS.REQUIRED.FEE
                    : ''
                  : ''
              }
            />
          ))}
        </DashboardFormCustomColumn>
      ))}
    </DashboardForm>
  );
};
